import { useMemo } from 'react';
import { osloDateString } from '@/lib/oslo-time';
import { resolvePeriod } from '@/lib/budget-deviation-calc';
import { computeApprovedTaskDeviations } from '@/lib/approved-task-deviation-calc';
import { closedSalesByMonth } from '@/lib/dashboard-bf-calc';
import { buildTeamFaktModel } from '@/components/dashboard/widgets/team-fakt-model';
import type { AgentToolInputs } from '@/lib/maaltall-agent/tools';
import type { Person } from '@/lib/maaltall-agent/pseudonym';
import { useAllUsers } from '@/hooks/use-users';
import { useBillingRates } from '@/hooks/use-billing-rates';
import { useCapacityBreakdown } from '@/hooks/use-capacity-breakdown';
import { useReportingReconciliation } from '@/hooks/use-reporting-reconciliation';
import { useBudgetDeviations } from '@/hooks/use-budget-deviations';
import { useBonusGoals } from '@/hooks/use-bonus-goals';
import { useInvoiceForecast } from '@/hooks/use-invoice-forecast';
import { useOffersInPipe } from '@/hooks/use-offers-in-pipe';
import { useMaaltallFindings } from '@/hooks/use-maaltall-findings';
import { useTotalRevenueYtd } from '@/hooks/use-total-revenue-ytd';
import { useTotalResultYtd } from '@/hooks/use-total-result-ytd';
import { useSalesDashboardData } from '@/hooks/use-sales-dashboard-data';

/**
 * Dataene tall-agenten svarer fra (VIB-2992): de samme hookene og
 * beregningene som widgetene på Måltall, så cachen deles og tallene er like.
 */
export function useMaaltallAgentInputs(): { inputs: AgentToolInputs; people: Person[] } {
  const year = new Date().getFullYear();
  const users = useAllUsers();
  const billing = useBillingRates(year);
  const breakdown = useCapacityBreakdown(year);
  const reporting = useReportingReconciliation(year);
  const deviations = useBudgetDeviations();
  const bonus = useBonusGoals(year);
  const forecast = useInvoiceForecast();
  const pipe = useOffersInPipe(true);
  const { findings } = useMaaltallFindings(year);
  const revenue = useTotalRevenueYtd();
  const result = useTotalResultYtd();
  const sales = useSalesDashboardData();
  const throughMonth = reporting.enabled ? reporting.reconciledThroughMonth : new Date().getMonth();

  const faktModel = useMemo(
    () => billing.data && breakdown.data
      ? buildTeamFaktModel({
          year, throughMonth, target: billing.data.teamTargetRate, teamRate: billing.data.teamYtdRate,
          employees: billing.data.employees, breakdown: breakdown.data,
        })
      : null,
    [billing.data, breakdown.data, throughMonth, year],
  );
  const taskDeviation = useMemo(() => {
    if (!deviations.data) return null;
    const d = deviations.data;
    return {
      ...computeApprovedTaskDeviations(d.approvedTaskAssignments, d.approvedTaskTimeEntries, resolvePeriod('ar', new Date())),
      names: new Map(d.users.map((u) => [u.userId, u.fullName])),
    };
  }, [deviations.data]);

  return {
    people: (users.data ?? []).map((u) => ({ id: u.id, name: u.full_name ?? '' })),
    inputs: {
      today: osloDateString(new Date()),
      faktModel,
      taskDeviation,
      goals: bonus.data?.goals ?? null,
      forecast: forecast.data?.months ?? null,
      pipe: pipe.isLoading ? null : { totals: pipe.totals, pipes: pipe.pipes },
      findings,
      revenue: revenue.data?.months ?? null,
      result: result.data?.available ? result.data.months : null,
      soldByMonth: sales.projects ? closedSalesByMonth(sales.projects, year).map((m) => m.estBfKr) : null,
    },
  };
}
